import * as Popover from '@radix-ui/react-popover'
import { ComponentProps, useEffect, useRef, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { useScrollHighlightedElementIntoView } from '../../../hooks/useScrollHighlightedElementIntoView'
import { Icon, IconName } from '../../Icon'
import Label from '../../app/design/Label'

type Props<OptionT> = {
    options: OptionT[]
    onSelectOption?: (option: OptionT) => void
    selectedOptionID?: string | null
    size?: 'small' | 'medium'
    className?: string
    maxDropdownHeight?: number | null
    align?: ComponentProps<typeof Popover.Content>['align']
    shouldRestrictContentToTriggerWidth?: boolean
    trigger?: React.ReactNode
    onOpenChange?: (open: boolean) => void
    isDisabled?: boolean
    // There is a known radix-ui bug with scrolling content in portals in dialogs. Use this
    // param to disable the portal if in that scenario. https://github.com/radix-ui/primitives/issues/2028
    shouldPortal?: boolean
}

type _SelectOption = {
    id: string
    title: string
    subtitle?: string
    icon?: IconName
    isDanger?: boolean
}

export const Select = <OptionT extends _SelectOption>({
    options,
    onSelectOption,
    selectedOptionID,
    size = 'small',
    className,
    maxDropdownHeight = 256,
    align = 'start',
    shouldRestrictContentToTriggerWidth = false,
    trigger,
    onOpenChange,
    shouldPortal = true,
    isDisabled = false,
}: Props<OptionT>) => {
    const [isOpen, setIsOpen] = useState(false)

    const [highlightedItemIndex, setHighlightedItemIndex] = useState(0)

    const scrollingContainerRef = useRef<HTMLDivElement>(null)

    useScrollHighlightedElementIntoView(scrollingContainerRef, [highlightedItemIndex, options])

    const selectedOption =
        selectedOptionID !== null
            ? options.find((option) => option.id === selectedOptionID)
            : undefined

    useEffect(() => {
        if (!isOpen || options?.length === undefined) return

        const abortController = new AbortController()

        document.addEventListener(
            'keydown',
            (event) => {
                event.preventDefault()
                event.stopPropagation()

                if (event.key === 'ArrowDown' || (event.key === 'n' && event.ctrlKey)) {
                    setHighlightedItemIndex((prev) => (prev + 1) % options.length)
                }
                else if (event.key === 'ArrowUp' || (event.key === 'p' && event.ctrlKey)) {
                    setHighlightedItemIndex((prev) => (prev - 1 + options.length) % options.length)
                }
                else if (event.key === 'Enter') {
                    const option = options[highlightedItemIndex]
                    if (option !== undefined) {
                        setIsOpen(false)
                        onSelectOption?.(option)
                    }
                }
                else if (event.key === 'Escape') {
                    setIsOpen(false)
                    onOpenChange?.(false)
                }
            },
            { signal: abortController.signal }
        )

        return () => abortController.abort()
    }, [isOpen, options, highlightedItemIndex, onOpenChange, onSelectOption])

    const content = (
        <Popover.Content
            sideOffset={8}
            align={align}
            avoidCollisions
            className="flex flex-col items-start justify-start overflow-y-auto rounded-lg bg-white p-2 shadow-xl"
            style={{
                maxHeight: maxDropdownHeight
                    ? `calc(min(var(--radix-popover-content-available-height), ${maxDropdownHeight}px) - 8px)`
                    : `calc(var(--radix-popover-content-available-height) - 8px)`,
                minWidth: `var(--radix-popover-trigger-width)`,
                maxWidth: shouldRestrictContentToTriggerWidth
                    ? `var(--radix-popover-trigger-width)`
                    : undefined,
            }}
            ref={scrollingContainerRef}
        >
            {options.map((option, optionIndex) => (
                <SelectOptionView
                    key={option.id}
                    option={option}
                    size={size}
                    isHighlighted={optionIndex === highlightedItemIndex}
                    onSelect={() => {
                        setIsOpen(false)
                        onOpenChange?.(false)
                        onSelectOption?.(option)
                    }}
                    onMouseEnter={() => setHighlightedItemIndex(optionIndex)}
                    isSelected={option.id === selectedOptionID}
                />
            ))}
        </Popover.Content>
    )

    return (
        <Popover.Root
            open={isOpen}
            onOpenChange={(o) => {
                setIsOpen(o)
                onOpenChange?.(o)
            }}
        >
            <Popover.Anchor asChild>
                <Popover.Trigger
                    asChild
                    onClick={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                        setIsOpen((prev) => {
                            onOpenChange?.(!prev)
                            return !prev
                        })
                    }}
                >
                    {trigger ?? (
                        <div
                            className={twm(
                                'hover:border-primary-700 hover:outline-primary-700 cursor-pointer rounded-lg border border-gray-500 bg-white outline-0 outline-gray-500 hover:outline-1',
                                isOpen && 'border-primary-700 outline-primary-700 outline-2',
                                isDisabled &&
                                    'cursor-not-allowed border-gray-200 hover:border-gray-200 hover:outline-0',
                                className
                            )}
                        >
                            <button
                                onClick={(event) => {
                                    if (isDisabled) return
                                    event.stopPropagation()
                                    event.preventDefault()
                                    setIsOpen((prev) => !prev)
                                }}
                                className={twm(
                                    'flex w-full cursor-pointer items-center justify-between rounded-lg px-4 text-left select-none',
                                    size === 'small' ? 'py-[5px]' : 'min-h-[46px] py-[11px]',
                                    isDisabled && 'cursor-not-allowed'
                                )}
                                disabled={isDisabled}
                            >
                                <div>
                                    <Label
                                        size={size === 'small' ? 'sm' : 'md'}
                                        className={twm(
                                            'min-w-0 cursor-pointer self-center truncate font-normal',
                                            isDisabled && 'cursor-not-allowed, text-gray-400'
                                        )}
                                    >
                                        {selectedOption?.title ?? 'None'}
                                    </Label>
                                    {selectedOption?.subtitle && (
                                        <Label
                                            size="sm"
                                            className="mt-0.5 block font-normal text-gray-600"
                                        >
                                            {selectedOption.subtitle}
                                        </Label>
                                    )}
                                </div>

                                <div className="ml-3 flex items-center justify-center">
                                    <Icon
                                        icon={
                                            size === 'small'
                                                ? 'chevronDown.small'
                                                : 'chevronDown.medium'
                                        }
                                        className={twm(
                                            'text-gray-700',
                                            `transition-transform duration-200`,
                                            isOpen && '-rotate-180',
                                            isDisabled && 'text-gray-400'
                                        )}
                                    />
                                </div>
                            </button>
                        </div>
                    )}
                </Popover.Trigger>
            </Popover.Anchor>
            {shouldPortal ? <Popover.Portal>{content}</Popover.Portal> : content}
        </Popover.Root>
    )
}

const SelectOptionView = ({
    option,
    size,
    isHighlighted,
    onSelect,
    onMouseEnter,
    isSelected,
}: {
    option: _SelectOption
    isSelected: boolean
    size?: 'small' | 'medium'
    isHighlighted: boolean
    onSelect?: () => void
    onMouseEnter?: () => void
}) => {
    return (
        <button
            className={twm(
                'w-full cursor-pointer rounded-lg px-4 py-3 text-left select-none',
                size === 'small' ? 'py-2' : 'py-3',
                isHighlighted && 'bg-gray-700/4'
            )}
            onMouseEnter={onMouseEnter}
            onMouseDown={(e) => {
                // prevent bubbling up to the parent
                // necessary in cases like table rows where the row itself has an onClick
                e.preventDefault()
                onSelect?.()
            }}
            data-is-highlighted={isHighlighted}
        >
            <div className="flex items-center justify-between gap-x-3">
                <div className="flex items-center gap-x-3">
                    {option.isDanger &&
                        (!option.icon ? (
                            <Icon icon={`close.small`} className="text-danger-400 shrink-0" />
                        ) : (
                            <Icon
                                icon={`${option.icon}.small`}
                                className="text-danger-400 shrink-0"
                            />
                        ))}
                    {option.icon && !option.isDanger && (
                        <Icon icon={`${option.icon}.small`} className="shrink-0 text-gray-500" />
                    )}
                    <div>
                        <Label
                            size={size === 'small' ? 'sm' : 'md'}
                            className={twm(
                                'block min-w-0 cursor-pointer self-center truncate font-normal',
                                option.isDanger && 'text-danger-400'
                            )}
                        >
                            {option.title}
                        </Label>
                        {option.subtitle && (
                            <Label size="sm" className="mt-0.5 block font-normal text-gray-600">
                                {option.subtitle}
                            </Label>
                        )}
                    </div>
                </div>
                {isSelected && <Icon icon={`checkmark.small`} className="text-primary-600" />}
            </div>
        </button>
    )
}
